import MarkerClustererPlus, { ClusterIcon } from 'marker-clusterer-plus-es2015';

const flatten = (array) => (Array.isArray(array) ? [].concat(...array.map(flatten)) : array);

export class MarkerClusterer extends MarkerClustererPlus {
    constructor(...args) {
        super(...args, [], {
            enableRetinaIcons: false,
            zoomOnClick: false,
            gridSize: 70,
        });

        google.maps.event.addListener(this, 'click', (cluster) => {
            const currentZoom = cluster.getMap().getZoom();
            const maxZoom = cluster.getMap().maxZoom;

            // if the current zoom is the same as the max zoom and we still have clusters
            // it meas that some markers have the same exact location...
            // and there is no way to show them alongside each other
            // this workaround will toggle through the clustered markers and click them for subsequent clicks on the cluster
            if (currentZoom === maxZoom) {
                const markersCount = cluster.getMarkers().length;
                if (!cluster.clickCount) cluster.clickCount = 0;

                new google.maps.event.trigger(cluster.getMarkers()[cluster.clickCount % markersCount], 'click');

                cluster.clickCount++;
            }

            this.zoomIntoCluster(cluster);
        });
    }

    // patch ClusterIcon.prototype.show to be able to render custom HTML as marker
    // patch ClusterIcon.prototype.onAdd to not use deprecated addDomListener
    setRenderClusterCallback(callback) {
        ClusterIcon.prototype.show = function () {
            if (this.div_) {
                const pos = this.getPosFromLatLng_(this.center_);
                this.div_.style.cssText = this.createCss(pos);

                if (typeof this.sums_.title === 'undefined' || this.sums_.title === '') {
                    this.div_.title = this.cluster_.getMarkerClusterer().getTitle();
                } else {
                    this.div_.title = this.sums_.title;
                }
                this.div_.style.display = '';

                this.div_.innerHTML = callback(this.sums_.text);
            }
            this.visible_ = true;
        };

        ClusterIcon.prototype.onAdd = function () {
            var cClusterIcon = this;
            var cMouseDownInCluster;
            var cDraggingMapByCluster;

            this.div_ = document.createElement("div");
            this.div_.className = this.className_;
            if (this.visible_) {
                this.show();
            }

            this.getPanes().overlayMouseTarget.appendChild(this.div_);

            // Fix for Issue 157
            this.boundsChangedListener_ = google.maps.event.addListener(this.getMap(), "bounds_changed", function () {
                cDraggingMapByCluster = cMouseDownInCluster;
            });

            this.div_.addEventListener("mousedown", function () {
                cMouseDownInCluster = true;
                cDraggingMapByCluster = false;
            });

            this.div_.addEventListener("click", function (e) {
                cMouseDownInCluster = false;
                if (!cDraggingMapByCluster) {
                    var theBounds;
                    var mz;
                    var mc = cClusterIcon.cluster_.getMarkerClusterer();
                    /**
                     * This event is fired when a cluster marker is clicked.
                     * @name MarkerClusterer#click
                     * @param {Cluster} c The cluster that was clicked.
                     * @event
                     */
                    google.maps.event.trigger(mc, "click", cClusterIcon.cluster_);
                    google.maps.event.trigger(mc, "clusterclick", cClusterIcon.cluster_); // deprecated name

                    // The default click handler follows. Disable it by setting
                    // the zoomOnClick property to false.
                    if (mc.getZoomOnClick()) {
                        // Zoom into the cluster.
                        mz = mc.getMaxZoom();
                        theBounds = cClusterIcon.cluster_.getBounds();
                        mc.getMap().fitBounds(theBounds);
                        // There is a fix for Issue 170 here:
                        setTimeout(function () {
                            mc.getMap().fitBounds(theBounds);
                            // Don't zoom beyond the max zoom level
                            if (mz !== null && mc.getMap().getZoom() > mz) {
                                mc.getMap().setZoom(mz + 1);
                            }
                        }, 100);
                    }

                    // Prevent event propagation to the map:
                    e.cancelBubble = true;
                    if (e.stopPropagation) {
                        e.stopPropagation();
                    }
                }
            });

            this.div_.addEventListener("mouseover", function () {
                var mc = cClusterIcon.cluster_.getMarkerClusterer();
                /**
                 * This event is fired when the mouse moves over a cluster marker.
                 * @name MarkerClusterer#mouseover
                 * @param {Cluster} c The cluster that the mouse moved over.
                 * @event
                 */
                google.maps.event.trigger(mc, "mouseover", cClusterIcon.cluster_);
            });

            this.div_.addEventListener("mouseout", function () {
                var mc = cClusterIcon.cluster_.getMarkerClusterer();
                /**
                 * This event is fired when the mouse moves out of a cluster marker.
                 * @name MarkerClusterer#mouseout
                 * @param {Cluster} c The cluster that the mouse moved out of.
                 * @event
                 */
                google.maps.event.trigger(mc, "mouseout", cClusterIcon.cluster_);
            });
        };
    }

    preventNextClustering() {
        this._preventRedraw = true;
    }

    /**
     * Overwrites onAdd implementation of markerclustererplus.js because it sucks!
     * With this change it will be possible to intercept re-clustering
     */
    onAdd() {
        var _this = this;
        this.activeMap_ = this.getMap();
        this.ready_ = true;
        this.repaint();
        this.prevZoom_ = this.getMap().getZoom();
        // Add the map event listeners
        this.listeners_ = [
            google.maps.event.addListener(this.getMap(), 'zoom_changed', function () {
                var map = _this.getMap(); // eslint-disable-line @typescript-eslint/no-explicit-any
                // Fix for bug #407
                // Determines map type and prevents illegal zoom levels
                var minZoom = map.minZoom || 0;
                var maxZoom = Math.min(map.maxZoom || 100, map.mapTypes[map.getMapTypeId()].maxZoom);
                var zoom = Math.min(Math.max(_this.getMap().getZoom(), minZoom), maxZoom);
                if (_this.prevZoom_ != zoom) {
                    _this.prevZoom_ = zoom;
                    _this.resetViewport_(false);
                }
            }),
            google.maps.event.addListener(this.getMap(), 'idle', function () {
                _this.redraw_();
            }),
        ];
    }

    getClustersInBounds() {
        const bounds = this.getMap().getBounds();

        if (!bounds) return;

        const clusters = this.getClusters().filter((cluster) => {
            return cluster.getSize() > 1 && bounds.contains(cluster.center_);
        });

        // add click handlers to clusters, to manually trigger to zoom into cluster
        for (let cluster of clusters) {
            cluster.triggerClick = () => {
                cluster.clusterIcon_.div_.click();
            };
            cluster.clusterIcon_.image_ = cluster.clusterIcon_.div_.querySelector('img');
        }

        return clusters;
    }

    clickClusterThatContainsId(id) {
        const cluster = this.getClusterWithId(id);

        if (cluster) {
            cluster.triggerClick();
        }
    }

    zoomIntoCluster(cluster) {
        if (!cluster) return;

        // Zoom into the cluster.
        const clusterBounds = cluster.getBounds();
        const currentZoom = cluster.getMap().getZoom();

        cluster.getMap().fitBounds(clusterBounds);

        setTimeout(function () {
            // In some cases the newly calculated bounds wont't fit onto the new bounds of the map and the map will not zoom
            // Therefore we need to zoom in manually
            if (currentZoom === cluster.getMap().getZoom()) {
                cluster.getMap().fitBounds(clusterBounds);
                cluster.getMap().setZoom(currentZoom + 1);
            }
        }, 100);
    }

    mouseOverClusterWithId(id) {
        const cluster = this.getClusterWithId(id);
        if (cluster) {
            if (cluster.clusterIcon_.image_) cluster.clusterIcon_.image_.src = clusterImageHover;
            cluster.clusterIcon_.div_.classList.add('hover');
        }
    }

    mouseOutClusterWithId(id) {
        const cluster = this.getClusterWithId(id);
        if (cluster) {
            if (cluster.clusterIcon_.image_) cluster.clusterIcon_.image_.src = clusterImageDefault;
            cluster.clusterIcon_.div_.classList.remove('hover');
        }
    }

    getMarkersInBounds() {
        const bounds = this.getMap().getBounds();
        if (!bounds) {
            return;
        }

        //get all clusters in bounds
        return this.getMarkers().filter((marker) => {
            return this.isMarkerInBounds_(marker, bounds);
        });
    }

    getSingleMarkersInBounds() {
        const markers = this.getMarkersInBounds();
        const clusters = this.getClustersInBounds();

        if (!markers || !clusters) {
            return;
        }

        //get all clusters in bounds
        const clustered = flatten(clusters.map((cluster) => cluster.getMarkers().map((marker) => marker)));

        return markers.filter((marker) => {
            return clustered.indexOf(marker) === -1;
        });
    }

    getClusterWithId(id) {
        return this.getClusters().find((cluster) => {
            const markerIds = cluster.getMarkers().map((marker) => marker.id);

            if (markerIds.indexOf(id) > -1) {
                return cluster;
            }
        });
    }

    getClusterId(cluster) {
        return cluster.getMarkers()[0].id;
    }
}

export default MarkerClusterer;
